import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import type { BetaAllowlistEntry } from '../types/system';
import { Switch } from './Switch';
import '../styles/system-status.css';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Admin-Pseudo': pseudo,
    },
  });
}

interface SearchPlayer {
  pseudo: string;
  pseudoDisplay: string;
  photoUrl?: string | null;
}

interface BetaAllowlistPanelProps {
  session: Session;
  entries: BetaAllowlistEntry[];
  busy: boolean;
  defaultSendWelcomeDm?: boolean;
  onChanged: () => Promise<void>;
  onFeedback: (message: string) => void;
  onBusyChange: (busy: boolean) => void;
}

function formatRelativeActivity(entry: BetaAllowlistEntry): string {
  const iso = entry.lastNeoSeenAt || entry.lastLoginAt;
  if (!iso) return 'Jamais';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Jamais';

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'À l\'instant';
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 48) return `Il y a ${diffHours} h`;

  return date.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit',
  });
}

function activitySourceLabel(entry: BetaAllowlistEntry): string {
  if (entry.lastNeoSeenAt) return 'Neo';
  if (entry.lastLoginAt) return 'Webtarot';
  return '';
}

type AllowlistFilter = 'all' | 'online' | 'never_connected' | 'inactive_7d' | 'missing';
type AllowlistSort = 'name' | 'activity_desc' | 'never_first';

function activityTimestamp(entry: BetaAllowlistEntry): number {
  const iso = entry.lastNeoSeenAt || entry.lastLoginAt;
  if (!iso) return 0;
  const ts = new Date(iso).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function isNeverConnected(entry: BetaAllowlistEntry): boolean {
  return !entry.lastNeoSeenAt && !entry.lastLoginAt;
}

const INACTIVE_MS = 7 * 24 * 60 * 60 * 1000;

function isInactive7Days(entry: BetaAllowlistEntry): boolean {
  const ts = activityTimestamp(entry);
  if (ts === 0) return true;
  return Date.now() - ts >= INACTIVE_MS;
}

function filterEntries(entries: BetaAllowlistEntry[], filter: AllowlistFilter): BetaAllowlistEntry[] {
  switch (filter) {
    case 'online':
      return entries.filter(e => e.isOnline);
    case 'never_connected':
      return entries.filter(isNeverConnected);
    case 'inactive_7d':
      return entries.filter(isInactive7Days);
    case 'missing':
      return entries.filter(e => !e.accountExists);
    default:
      return entries;
  }
}

function sortEntries(entries: BetaAllowlistEntry[], sort: AllowlistSort): BetaAllowlistEntry[] {
  const copy = [...entries];
  switch (sort) {
    case 'never_first':
      return copy.sort((a, b) => {
        const aNever = isNeverConnected(a);
        const bNever = isNeverConnected(b);
        if (aNever !== bNever) return aNever ? -1 : 1;
        return a.pseudoDisplay.localeCompare(b.pseudoDisplay, 'fr');
      });
    case 'activity_desc':
      return copy.sort((a, b) => activityTimestamp(b) - activityTimestamp(a));
    default:
      return copy.sort((a, b) => a.pseudoDisplay.localeCompare(b.pseudoDisplay, 'fr'));
  }
}

export default function BetaAllowlistPanel({
  session,
  entries,
  busy,
  defaultSendWelcomeDm = false,
  onChanged,
  onFeedback,
  onBusyChange,
}: BetaAllowlistPanelProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchPlayer[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [filter, setFilter] = useState<AllowlistFilter>('all');
  const [sort, setSort] = useState<AllowlistSort>('never_first');
  const [sendWelcomeDm, setSendWelcomeDm] = useState(defaultSendWelcomeDm);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSendWelcomeDm(defaultSendWelcomeDm);
  }, [defaultSendWelcomeDm]);

  const allowlistSet = useMemo(
    () => new Set(entries.map(e => e.pseudo.toLowerCase())),
    [entries],
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchPlayers = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      setSearchError(null);
      return;
    }

    setLoadingSearch(true);
    setSearchError(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(q.trim())}&limit=12&compet=false`,
      );
      const json = await res.json() as { players?: SearchPlayer[] };
      const filtered = (json.players || []).filter(
        p => !allowlistSet.has(p.pseudo.toLowerCase()),
      );
      setResults(filtered);
    } catch {
      setResults([]);
      setSearchError('Recherche indisponible');
    } finally {
      setLoadingSearch(false);
    }
  }, [allowlistSet]);

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    setShowDropdown(true);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { void searchPlayers(value); }, 250);
  }, [searchPlayers]);

  const addPseudo = async (player: SearchPlayer) => {
    onBusyChange(true);
    setSearchError(null);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/allowlist?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ add: [player.pseudo], sendWelcomeDm }),
        },
      );
      const data = await res.json() as {
        message?: string;
        error?: string;
        welcomeDmResults?: Array<{ pseudo: string; sent: boolean; reason?: string }>;
      };
      if (!res.ok) {
        const msg = data.error === 'already_in_allowlist'
          ? `${player.pseudoDisplay} est déjà dans la whitelist`
          : data.message || data.error || 'Erreur lors de l\'ajout';
        setSearchError(msg);
        onFeedback(msg);
        return;
      }

      setQuery('');
      setResults([]);
      setShowDropdown(false);
      const dmResult = data.welcomeDmResults?.[0];
      if (sendWelcomeDm && dmResult?.sent) {
        onFeedback(`${player.pseudoDisplay} ajouté — message de bienvenue envoyé`);
      } else if (sendWelcomeDm && dmResult && !dmResult.sent) {
        onFeedback(`${player.pseudoDisplay} ajouté — message non envoyé (${dmResult.reason ?? '?'})`);
      } else {
        onFeedback(`${player.pseudoDisplay} ajouté`);
      }
      await onChanged();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      setSearchError(msg);
      onFeedback(msg);
    } finally {
      onBusyChange(false);
    }
  };

  const removePseudo = async (pseudo: string, pseudoDisplay: string) => {
    onBusyChange(true);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/allowlist/${encodeURIComponent(pseudo)}?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        { method: 'DELETE' },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      onFeedback(`${pseudoDisplay} retiré`);
      await onChanged();
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      onBusyChange(false);
    }
  };

  const missingCount = entries.filter(e => !e.accountExists).length;
  const neverConnectedCount = entries.filter(isNeverConnected).length;
  const inactive7dCount = entries.filter(isInactive7Days).length;
  const onlineCount = entries.filter(e => e.isOnline).length;

  const visibleEntries = useMemo(
    () => sortEntries(filterEntries(entries, filter), sort),
    [entries, filter, sort],
  );

  return (
    <div className="beta-allowlist-panel" ref={panelRef}>
      <label className="beta-welcome-dm-toggle beta-allowlist-dm-toggle">
        <span>Envoyer le message privé de bienvenue (panurge) à l&apos;ajout</span>
        <Switch
          checked={sendWelcomeDm}
          onChange={setSendWelcomeDm}
          disabled={busy}
        />
      </label>

      <div
        className={`beta-allowlist-search-wrap${showDropdown && query.trim().length >= 2 ? ' beta-allowlist-search-wrap--open' : ''}`}
      >
        <input
          type="search"
          className="beta-allowlist-search"
          placeholder="Rechercher un joueur à ajouter…"
          value={query}
          onChange={e => handleQueryChange(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          disabled={busy}
          autoComplete="off"
        />
        {loadingSearch && <span className="beta-allowlist-search-spinner" aria-hidden>…</span>}

        {showDropdown && query.trim().length >= 2 && (
          <div className="beta-allowlist-dropdown">
            {results.length > 0 ? results.map(player => (
              <button
                key={player.pseudo}
                type="button"
                className="beta-allowlist-dropdown-item"
                onMouseDown={e => {
                  e.preventDefault();
                  void addPseudo(player);
                }}
                disabled={busy}
              >
                <PlayerAvatar pseudo={player.pseudo} size={28} photoUrl={player.photoUrl} />
                <span className="beta-allowlist-dropdown-name">{player.pseudoDisplay}</span>
                <span className="beta-allowlist-dropdown-action">Ajouter</span>
              </button>
            )) : !loadingSearch && (
              <div className="beta-allowlist-dropdown-empty">
                {searchError || 'Aucun joueur trouvé'}
              </div>
            )}
          </div>
        )}
      </div>

      {searchError && (
        <p className="beta-allowlist-error">{searchError}</p>
      )}

      {entries.length === 0 ? (
        <p className="admin-system-ops-meta">Aucun bêta-testeur pour l&apos;instant.</p>
      ) : (
        <>
          <div className="beta-allowlist-summary">
            <span>{entries.length} bêta-testeur(s)</span>
            {onlineCount > 0 && <span>{onlineCount} en ligne</span>}
            {neverConnectedCount > 0 && (
              <span className="beta-allowlist-warning">
                {neverConnectedCount} jamais connecté(s)
              </span>
            )}
            {inactive7dCount > 0 && (
              <span className="beta-allowlist-warning">
                {inactive7dCount} inactif(s) 7 j+
              </span>
            )}
            {missingCount > 0 && (
              <span className="beta-allowlist-warning">
                {missingCount} compte(s) introuvable(s)
              </span>
            )}
          </div>

          <div className="beta-allowlist-toolbar">
            <div className="beta-allowlist-filters" role="tablist" aria-label="Filtrer la whitelist">
              {([
                ['all', 'Tous'],
                ['online', 'En ligne'],
                ['never_connected', 'Jamais connectés'],
                ['inactive_7d', 'Inactifs 7 j+'],
                ['missing', 'Introuvables'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={filter === key}
                  className={`beta-allowlist-filter${filter === key ? ' beta-allowlist-filter--active' : ''}`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <label className="beta-allowlist-sort">
              <span>Trier</span>
              <select
                value={sort}
                onChange={e => setSort(e.target.value as AllowlistSort)}
                disabled={busy}
              >
                <option value="never_first">Jamais connectés d&apos;abord</option>
                <option value="activity_desc">Activité récente</option>
                <option value="name">Nom</option>
              </select>
            </label>
          </div>

          {visibleEntries.length === 0 ? (
            <p className="admin-system-ops-meta">Aucun joueur pour ce filtre.</p>
          ) : (
          <div className="beta-allowlist-table-wrap">
            <table className="beta-allowlist-table">
              <thead>
                <tr>
                  <th>Joueur</th>
                  <th>Statut</th>
                  <th>Donnes</th>
                  <th>Msgs chat</th>
                  <th>Dernière activité</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map(entry => (
                  <tr
                    key={entry.pseudo}
                    className={[
                      !entry.accountExists ? 'beta-allowlist-row--missing' : '',
                      isNeverConnected(entry) ? 'beta-allowlist-row--never' : '',
                      isInactive7Days(entry) && !isNeverConnected(entry) ? 'beta-allowlist-row--inactive' : '',
                    ].filter(Boolean).join(' ') || undefined}
                  >
                    <td>
                      <div className="beta-allowlist-player">
                        <PlayerAvatar pseudo={entry.pseudo} size={32} photoUrl={entry.photoUrl} />
                        <div>
                          <div className="beta-allowlist-player-name">{entry.pseudoDisplay}</div>
                          {!entry.accountExists && (
                            <div className="beta-allowlist-player-warning">Compte supprimé ou introuvable</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {entry.isOnline ? (
                        <span className="beta-allowlist-badge beta-allowlist-badge--online">
                          <span className="status-dot connected" />
                          En ligne
                        </span>
                      ) : entry.accountExists ? (
                        <span className="beta-allowlist-badge beta-allowlist-badge--offline">
                          <span className="status-dot offline" />
                          Hors ligne
                        </span>
                      ) : (
                        <span className="beta-allowlist-badge beta-allowlist-badge--missing">
                          Introuvable
                        </span>
                      )}
                    </td>
                    <td className="beta-allowlist-metric">{entry.dealsCompleted}</td>
                    <td className="beta-allowlist-metric">{entry.chatMessages}</td>
                    <td>
                      <div className="beta-allowlist-activity">
                        {formatRelativeActivity(entry)}
                        {activitySourceLabel(entry) && (
                          <span className="beta-allowlist-activity-source">
                            {activitySourceLabel(entry)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="beta-allowlist-actions">
                      <button
                        type="button"
                        className="beta-allowlist-remove"
                        title={`Retirer ${entry.pseudoDisplay}`}
                        aria-label={`Retirer ${entry.pseudoDisplay}`}
                        disabled={busy}
                        onClick={() => void removePseudo(entry.pseudo, entry.pseudoDisplay)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
}
